<template>
    <div class="content-wrapper">
        <div class="row">
            <div class="col-sm-12 col-md-6">
                <app-breadcrumb :page-title="$t('attendance_reports')" :icon="'file-alt'" />
            </div>
            <div class="col-sm-12 col-md-6 breadcrumb-side-button">
                <div class="float-md-right mb-3 mb-sm-3 mb-md-0">
                    <button 
                        type="button" 
                        class="btn btn-secondary btn-with-shadow"
                        @click="goBack"
                    >
                        <i class="fas fa-arrow-left mr-2"></i>
                        {{ $t('back') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Report Configuration -->
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        <h5 class="mb-0">{{ $t('report_configuration') }}</h5>
                    </div>
                    <div class="card-body">
                        <form @submit.prevent="generateReport">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label for="report_type">{{ $t('report_type') }}</label>
                                        <select v-model="reportConfig.type" id="report_type" class="form-control">
                                            <option value="daily">{{ $t('daily_report') }}</option>
                                            <option value="weekly">{{ $t('weekly_report') }}</option>
                                            <option value="monthly">{{ $t('monthly_report') }}</option>
                                            <option value="custom">{{ $t('custom_period') }}</option>
                                        </select>
                                    </div>
                                </div>

                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label for="export_format">{{ $t('export_format') }}</label>
                                        <select v-model="reportConfig.format" id="export_format" class="form-control">
                                            <option value="pdf">PDF</option>
                                            <option value="excel">Excel (.xlsx)</option>
                                            <option value="csv">CSV</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div class="row" v-if="reportConfig.type === 'daily' || reportConfig.type === 'custom'">
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label for="start_date">{{ $t('start_date') }}</label>
                                        <input 
                                            type="date" 
                                            v-model="reportConfig.start_date" 
                                            id="start_date" 
                                            class="form-control"
                                        >
                                    </div>
                                </div>

                                <div class="col-md-6" v-if="reportConfig.type === 'custom'">
                                    <div class="form-group">
                                        <label for="end_date">{{ $t('end_date') }}</label>
                                        <input 
                                            type="date" 
                                            v-model="reportConfig.end_date" 
                                            id="end_date" 
                                            class="form-control"
                                        >
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label for="location">{{ $t('location') }}</label>
                                        <select v-model="reportConfig.location_id" id="location" class="form-control">
                                            <option value="">{{ $t('all_locations') }}</option>
                                            <option 
                                                v-for="location in locations" 
                                                :key="location.id" 
                                                :value="location.id"
                                            >
                                                {{ location.name }}
                                            </option>
                                        </select>
                                    </div>
                                </div>

                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label for="department">{{ $t('department') }}</label>
                                        <select v-model="reportConfig.department_id" id="department" class="form-control">
                                            <option value="">{{ $t('all_departments') }}</option>
                                            <option 
                                                v-for="department in departments" 
                                                :key="department.id" 
                                                :value="department.id"
                                            >
                                                {{ department.name }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <!-- Report Options -->
                            <div class="row">
                                <div class="col-12">
                                    <div class="form-group">
                                        <label>{{ $t('include_in_report') }}</label>
                                        <div class="row">
                                            <div class="col-md-3">
                                                <div class="form-check">
                                                    <input 
                                                        type="checkbox" 
                                                        v-model="reportConfig.include_summary" 
                                                        id="include_summary" 
                                                        class="form-check-input"
                                                    >
                                                    <label for="include_summary" class="form-check-label">{{ $t('summary_statistics') }}</label>
                                                </div>
                                            </div>
                                            <div class="col-md-3">
                                                <div class="form-check">
                                                    <input 
                                                        type="checkbox" 
                                                        v-model="reportConfig.include_details" 
                                                        id="include_details" 
                                                        class="form-check-input"
                                                    >
                                                    <label for="include_details" class="form-check-label">{{ $t('detailed_records') }}</label>
                                                </div>
                                            </div>
                                            <div class="col-md-3">
                                                <div class="form-check">
                                                    <input 
                                                        type="checkbox" 
                                                        v-model="reportConfig.include_charts" 
                                                        id="include_charts" 
                                                        class="form-check-input"
                                                    >
                                                    <label for="include_charts" class="form-check-label">{{ $t('charts_graphs') }}</label>
                                                </div>
                                            </div>
                                            <div class="col-md-3">
                                                <div class="form-check">
                                                    <input 
                                                        type="checkbox" 
                                                        v-model="reportConfig.include_overtime" 
                                                        id="include_overtime" 
                                                        class="form-check-input"
                                                    >
                                                    <label for="include_overtime" class="form-check-label">{{ $t('overtime_analysis') }}</label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="text-center">
                                <button type="submit" class="btn btn-primary btn-lg" :disabled="isGenerating">
                                    <i class="fas fa-file-export mr-2"></i>
                                    <span v-if="isGenerating">{{ $t('generating') }}...</span>
                                    <span v-else>{{ $t('generate_report') }}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>

        <!-- Recent Reports -->
        <div class="row mt-4">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        <h5 class="mb-0">{{ $t('recent_reports') }}</h5>
                    </div>
                    <div class="card-body">
                        <div v-if="recentReports.length === 0" class="text-center py-4">
                            <i class="fas fa-file-alt fa-3x text-muted mb-3"></i>
                            <p class="text-muted">{{ $t('no_recent_reports') }}</p>
                        </div>
                        
                        <div v-else class="table-responsive">
                            <table class="table table-striped">
                                <thead>
                                    <tr>
                                        <th>{{ $t('report_name') }}</th>
                                        <th>{{ $t('type') }}</th>
                                        <th>{{ $t('period') }}</th>
                                        <th>{{ $t('generated_at') }}</th>
                                        <th>{{ $t('actions') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="report in recentReports" :key="report.id">
                                        <td>{{ report.name }}</td>
                                        <td>
                                            <span class="badge badge-info">{{ report.type }}</span>
                                        </td>
                                        <td>{{ report.period }}</td>
                                        <td>{{ formatDate(report.generated_at) }}</td>
                                        <td>
                                            <button 
                                                type="button" 
                                                class="btn btn-sm btn-primary mr-2"
                                                @click="downloadReport(report)"
                                            >
                                                <i class="fas fa-download"></i>
                                            </button>
                                            <button 
                                                type="button" 
                                                class="btn btn-sm btn-danger"
                                                @click="deleteReport(report)"
                                            >
                                                <i class="fas fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CoreLibrary from '../../../../core/helpers/CoreLibrary';

export default {
    extends: CoreLibrary,
    name: "AttendanceReports",
    data() {
        return {
            reportConfig: {
                type: 'daily',
                format: 'pdf',
                start_date: window.moment ? window.moment().format('YYYY-MM-DD') : new Date().toISOString().split('T')[0],
                end_date: window.moment ? window.moment().format('YYYY-MM-DD') : new Date().toISOString().split('T')[0],
                location_id: '',
                department_id: '',
                include_summary: true,
                include_details: true,
                include_charts: false,
                include_overtime: false
            },
            locations: [],
            departments: [],
            recentReports: [],
            isGenerating: false
        }
    },
    mounted() {
        this.loadLocations();
        this.loadDepartments();
        this.loadRecentReports();
    },
    methods: {
        async loadLocations() {
            try {
                const response = await this.axiosGet('/api/locations');
                this.locations = response.data;
            } catch (error) {
                console.error('Error loading locations:', error);
            }
        },

        async loadDepartments() {
            try {
                const response = await this.axiosGet('/api/departments');
                this.departments = response.data;
            } catch (error) {
                console.error('Error loading departments:', error);
            }
        },

        async loadRecentReports() {
            try {
                const response = await this.axiosGet('/attendances/recent-reports');
                this.recentReports = response.data;
            } catch (error) {
                console.error('Error loading recent reports:', error);
            }
        },

        async generateReport() {
            this.isGenerating = true;
            
            try {
                let url = '/attendances/export-summary';
                const params = new URLSearchParams();

                // Add parameters based on report type
                if (this.reportConfig.type === 'daily') {
                    params.append('date', this.reportConfig.start_date);
                } else if (this.reportConfig.type === 'weekly') {
                    const now = window.moment ? window.moment() : new Date();
                    const startOfWeek = window.moment ? now.startOf('week').format('YYYY-MM-DD') : this.getWeekStart(now);
                    const endOfWeek = window.moment ? window.moment().endOf('week').format('YYYY-MM-DD') : this.getWeekEnd(now);
                    params.append('start_date', startOfWeek);
                    params.append('end_date', endOfWeek);
                } else if (this.reportConfig.type === 'monthly') {
                    const now = window.moment ? window.moment() : new Date();
                    const startOfMonth = window.moment ? now.startOf('month').format('YYYY-MM-DD') : this.getMonthStart(now);
                    const endOfMonth = window.moment ? window.moment().endOf('month').format('YYYY-MM-DD') : this.getMonthEnd(now);
                    params.append('start_date', startOfMonth);
                    params.append('end_date', endOfMonth);
                } else if (this.reportConfig.type === 'custom') {
                    params.append('start_date', this.reportConfig.start_date);
                    params.append('end_date', this.reportConfig.end_date);
                }

                // Add filters
                if (this.reportConfig.location_id) {
                    params.append('location_id', this.reportConfig.location_id);
                }
                if (this.reportConfig.department_id) {
                    params.append('department_id', this.reportConfig.department_id);
                }

                // Add format and options
                params.append('format', this.reportConfig.format);
                params.append('include_summary', this.reportConfig.include_summary ? '1' : '0');
                params.append('include_details', this.reportConfig.include_details ? '1' : '0');
                params.append('include_charts', this.reportConfig.include_charts ? '1' : '0');
                params.append('include_overtime', this.reportConfig.include_overtime ? '1' : '0');

                // Open report in new window
                window.open(`${url}?${params.toString()}`, '_blank');

                this.$toastr.s('', this.$t('report_generated_successfully'));
                this.loadRecentReports(); // Refresh recent reports
                
            } catch (error) {
                console.error('Error generating report:', error);
                this.$toastr.e('', this.$t('error_generating_report'));
            } finally {
                this.isGenerating = false;
            }
        },

        downloadReport(report) {
            window.open(report.download_url, '_blank');
        },

        async deleteReport(report) {
            if (confirm(this.$t('confirm_delete_report'))) {
                try {
                    await this.axiosDelete(`/attendances/reports/${report.id}`);
                    this.$toastr.s('', this.$t('report_deleted_successfully'));
                    this.loadRecentReports();
                } catch (error) {
                    console.error('Error deleting report:', error);
                    this.$toastr.e('', this.$t('error_deleting_report'));
                }
            }
        },

        formatDate(date) {
            return window.moment ? window.moment(date).format('DD/MM/YYYY HH:mm') : new Date(date).toLocaleString();
        },

        // Helper methods for date calculations when moment is not available
        getWeekStart(date) {
            const d = new Date(date);
            const day = d.getDay();
            const diff = d.getDate() - day;
            const start = new Date(d.setDate(diff));
            return start.toISOString().split('T')[0];
        },

        getWeekEnd(date) {
            const d = new Date(date);
            const day = d.getDay();
            const diff = d.getDate() - day + 6;
            const end = new Date(d.setDate(diff));
            return end.toISOString().split('T')[0];
        },

        getMonthStart(date) {
            const d = new Date(date);
            return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
        },

        getMonthEnd(date) {
            const d = new Date(date);
            return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0];
        },

        goBack() {
            window.history.back();
        }
    }
}
</script>

<style scoped>
.form-check {
    margin-bottom: 10px;
}

.card {
    border-radius: 10px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.btn-lg {
    padding: 12px 30px;
    font-size: 16px;
}
</style>
